import { formatCurrency } from '../utils/money'
import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import Navbar from '../components/Navbar'
import { useCartStore } from '../stores/cartStore'
import { assetUrl } from '../utils/assetUrl'

export default function Cart() {
  const { items, removeItem, getTotal, clearCart, syncCart, loading } = useCartStore()

  useEffect(() => {
    syncCart()
  }, [syncCart])

  if (loading && items.length === 0) {
    return (
      <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
        <Navbar />
        <main className="min-h-[calc(100vh-var(--navbar-height))] flex items-center justify-center">
          <div className="text-[var(--text-secondary)]">Loading cart...</div>
        </main>
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
        <Navbar />
        <main className="min-h-[calc(100vh-var(--navbar-height))]">
          <div className="container mx-auto px-4 py-16 text-center">
            <div className="text-6xl mb-4">🛒</div>
            <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-4">Your cart is empty</h1>
            <p className="text-[var(--text-secondary)] mb-8">Browse items and add them to your cart</p>
            <Link to="/browse" className="btn-primary py-3 px-8 rounded-lg inline-block">Browse Items</Link>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
      <Navbar />
      <main className="min-h-[calc(100vh-var(--navbar-height))]">
        <div className="container mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold">Shopping Cart ({items.length})</h1>
                <button onClick={() => clearCart()} className="text-red-500 hover:underline">Clear all</button>
              </div>
              <div className="space-y-4">
                {items.map(item => (
                  <div key={item.id} className="bg-white p-4 rounded-xl flex gap-4">
                    <div className="w-24 h-24 bg-[var(--bg-tint)] rounded-lg flex items-center justify-center overflow-hidden shrink-0">
                      {item.itemImage ? (
                        <img
                          src={assetUrl(item.itemImage)}
                          alt={item.itemName}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement!.innerText = '🔧'; }}
                        />
                      ) : (
                        <span className="text-3xl text-[var(--forest-green)]">🔧</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[var(--text-primary)] truncate">{item.itemName || 'Unnamed item'}</h3>
                      <p className="text-sm text-[var(--text-muted)]">{Number.isFinite(item.days) ? item.days : 0} days • {formatCurrency(item.dailyPrice)}/day</p>
                      <p className="text-sm text-[var(--text-muted)]">Rental: {formatCurrency(item.itemSubtotal)}</p>
                      <p className="text-sm text-[var(--text-muted)]">Deposit: {formatCurrency(item.deposit)}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-bold text-lg">{formatCurrency(item.totalPrice)}</p>
                      <button onClick={() => removeItem(item.id)} className="text-red-500 text-sm">Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl h-fit">
              <h2 className="text-xl font-bold mb-4">Order Summary</h2>
              <div className="space-y-2 mb-4 text-[var(--text-secondary)]">
                <div className="flex justify-between"><span>Rental subtotal</span><span>{formatCurrency(items.reduce((s, i) => s + (Number.isFinite(i.itemSubtotal) ? i.itemSubtotal : 0), 0))}</span></div>
                <div className="flex justify-between"><span>Service Fee (10%)</span><span>{formatCurrency(items.reduce((s, i) => s + (Number.isFinite(i.serviceFee) ? i.serviceFee : 0), 0))}</span></div>
                <div className="flex justify-between"><span>Deposits</span><span>{formatCurrency(items.reduce((s, i) => s + (Number.isFinite(i.deposit) ? i.deposit : 0), 0))}</span></div>
              </div>
              <div className="border-t pt-4 flex justify-between font-bold text-lg">
                <span>Total</span><span>{formatCurrency(getTotal())}</span>
              </div>
              <Link to="/checkout" className="btn-primary w-full py-3 rounded-lg mt-6 block text-center">Proceed to Checkout</Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
